import {
  ALL_MILESTONE_STATUSES_AFTER_SUPPORT,
  MILESTONE_STATUS,
  MILESTONE_STATUS_AFTER_SUPPORT_TYPE,
  MILESTONE_TYPE,
} from 'src/shared/constants/milestone-constants'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useParams } from 'react-router'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useClient } from 'urql'
import { Utils } from 'src/shared/utils/Utils'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Card } from '@mui/material'
import { Form } from 'react-final-form'
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CheckboxGroupField } from 'src/shared/form/control/CheckboxGroupField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { Mutation_Root, Query_Root } from 'src/@types/graphql'
import {
  milestoneConclusionDataQuery,
  updateMilestoneStatueAfterSupportQuery,
} from 'src/screens/shared/implementation/details/milestoneQueries'
import { requiredSelection } from 'src/shared/form/validation/validators'
import { ROUTES } from 'src/routing/routes'
import { UserContext } from 'src/user/UserContext'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'

interface Props {
  baseUrl: '/pf-kap' | '/pf-pgv'
}

type originType = 'SAVE' | 'SAVE_AND_BACK'

interface FormValues {
  selected: MILESTONE_STATUS_AFTER_SUPPORT_TYPE[]
}

export const FinalReportConclusionStatusAfterSupportEditPage = ({ baseUrl }: Props) => {
  const { projectId, milestoneId } = useParams()

  const notificationService = useNotificationService()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const process = Utils.resolveProcess(baseUrl)
  const milestonePath = Utils.resolveMilestonePath(MILESTONE_TYPE.FINAL_REPORT)

  const originRef = useRef<originType>('SAVE')

  const { user } = useContext(UserContext)
  const { getMessage } = useMessageSource()

  const [initialValues, setInitialValues] = useState<FormValues | undefined>()
  const [canEdit, setCanEdit] = useState<boolean>()

  const fetchData = useCallback(async () => {
    const { data } = await urqlClient
      .query<{ milestone_by_pk: Query_Root['milestone_by_pk'] }>(milestoneConclusionDataQuery, { milestoneId })
      .toPromise()

    if (data?.milestone_by_pk) {
      const cantonalSupport = (data.milestone_by_pk.status_after_support ?? []) as MILESTONE_STATUS_AFTER_SUPPORT_TYPE[]

      setInitialValues({
        selected: cantonalSupport,
      })
      setCanEdit(data.milestone_by_pk.status !== MILESTONE_STATUS.DONE && Utils.isInternalRole(user.roles))
    }
  }, [milestoneId, urqlClient, projectId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('edit-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const updateHandler = async (values: FormValues) => {
    const { data, error } = await urqlClient
      .mutation<{ update_milestone: Mutation_Root['update_milestone'] }>(updateMilestoneStatueAfterSupportQuery, {
        id: milestoneId,
        statusAfterSupport: values.selected,
      })
      .toPromise()

    if (error || data?.update_milestone?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else {
      setInitialValues({ selected: values.selected })
      notificationService.changesSaved()
      if (originRef.current === 'SAVE_AND_BACK') {
        onBack()
      }
    }
  }

  const onBack = () => {
    navigate(ROUTES.PfKapFinalReportMilestoneRoot.nested.Conclusion.params({ projectId, milestoneId, milestonePath }))
  }

  if (canEdit === false) {
    return <NotAuthorized inSecondLevelNavigationTab />
  }

  return (
    <ScreenLayout
      title={getMessage(`label.milestone.conclusion.status_after_support.edit`)}
      hasSecondLevelNavigation={false}
      onBack={onBack}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <HelpAndInstructions
          labelKey={`label.help.milestone.conclusion.status_after_support.${process.toLowerCase()}`}
        />

        <Card>
          <>
            {initialValues && (
              <Form<FormValues>
                initialValues={initialValues}
                onSubmit={updateHandler}
                render={({ handleSubmit }) => {
                  return (
                    <form onSubmit={handleSubmit} id={'edit-form'}>
                      <Fragment>
                        <CheckboxGroupField
                          required
                          label={getMessage('label.milestone.conclusion.status_after_support')}
                          name={'selected'}
                          validate={requiredSelection()}
                          data={ALL_MILESTONE_STATUSES_AFTER_SUPPORT.map((key) => {
                            return {
                              label: `${getMessage(`label.milestone.conclusion.status_after_support.${key}`)}`,
                              value: key,
                            }
                          })}
                        />
                      </Fragment>

                      <DirtyFormSpy />
                    </form>
                  )
                }}
              />
            )}
          </>
        </Card>
      </PageLayout>
    </ScreenLayout>
  )
}
